/**
 * Parse Dropbox Context Engine `get_response` JSON into flat source rows for UI.
 */
export type ContextEngineSourceRow = {index: string; title: string; url: string};

export function extractCeSourcesFromRaw(
  response: Record<string, unknown>
): ContextEngineSourceRow[] {
  const seen = new Set<string>();
  const out: ContextEngineSourceRow[] = [];

  const add = (doc: unknown) => {
    if (!doc || typeof doc !== 'object') {
      return;
    }
    const d = doc as Record<string, unknown>;
    const title = typeof d.title === 'string' ? d.title.trim() : '';
    const url = typeof d.url === 'string' ? d.url.trim() : '';
    let index: string | undefined;
    const idxRaw = d.index;
    if (idxRaw != null && idxRaw !== '') {
      index = typeof idxRaw === 'string' ? idxRaw.trim() : String(idxRaw);
    }
    if (!index) {
      const u = typeof d.uuid === 'string' ? d.uuid.trim() : '';
      if (u) {
        index = u.includes('-')
          ? u.split('-', 1)[0]!.toLowerCase()
          : u.length >= 8
            ? u.slice(0, 8).toLowerCase()
            : u.toLowerCase();
      }
    }
    const key = `${url}\0${title}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    if (title || url) {
      const indexStr =
        index != null && String(index).trim() !== ''
          ? String(index).trim()
          : String(out.length + 1);
      out.push({index: indexStr, title: title || url, url});
    }
  };

  const extendFrom = (lst: unknown) => {
    if (!Array.isArray(lst)) {
      return;
    }
    for (const c of lst) {
      add(c);
    }
  };

  const progressUpdates = response.progress_updates;
  if (Array.isArray(progressUpdates)) {
    for (const pu of progressUpdates) {
      if (pu && typeof pu === 'object') {
        extendFrom((pu as Record<string, unknown>).context);
      }
    }
  }
  extendFrom(response.citations);
  const messages = response.messages;
  if (Array.isArray(messages)) {
    for (const msg of messages) {
      if (!msg || typeof msg !== 'object') {
        continue;
      }
      const rec = msg as Record<string, unknown>;
      const mt = (rec.message_type ?? rec) as Record<string, unknown>;
      if (!mt || typeof mt !== 'object') {
        continue;
      }
      extendFrom(mt.citations);
      extendFrom(mt.context);
      if (Array.isArray(mt.cited)) {
        extendFrom(mt.cited);
      }
      const am = mt.assistant_message;
      if (am && typeof am === 'object') {
        const amd = am as Record<string, unknown>;
        extendFrom(amd.citations);
        extendFrom(amd.context);
        if (Array.isArray(amd.cited)) {
          extendFrom(amd.cited);
        }
      }
      const evalData = (rec.eval_data ?? mt.eval_data) as Record<string, unknown> | undefined;
      if (evalData && typeof evalData === 'object') {
        extendFrom(evalData.matched_documents);
      }
    }
  }
  return out;
}

/**
 * Strip the structured draft artifacts the workstream update agent embeds in its
 * assistant text so the chat bubble only shows the conversational preamble. The
 * draft body itself is surfaced separately in the dedicated update/editor area
 * (via `draft.content`), so leaving these in the chat duplicates the draft.
 */
export function stripDraftMarkdownArtifacts(text: string): string {
  return text
    .replace(/<companyos_draft_markdown>[\s\S]*?<\/companyos_draft_markdown>/g, '')
    .replace(/<companyos_draft_markdown>[\s\S]*$/g, '')
    .replace(/<!--\s*companyos_update_metadata[\s\S]*?-->/g, '')
    .replace(/<!--\s*companyos_update_metadata[\s\S]*$/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Last non-empty assistant_message text in CE `messages` (same as admin preview). */
export function lastAssistantTextFromCeResponse(payload: Record<string, unknown>): string {
  const messages = payload.messages;
  if (!Array.isArray(messages)) {
    return '';
  }
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (!msg || typeof msg !== 'object') {
      continue;
    }
    const rec = msg as Record<string, unknown>;
    const mt = (rec.message_type ?? rec) as Record<string, unknown>;
    if (!mt || typeof mt !== 'object') {
      continue;
    }
    const tag = (mt['.tag'] ?? mt.tag) as string | undefined;
    const am = mt.assistant_message as Record<string, unknown> | undefined;
    let text: string | undefined;
    if (tag === 'assistant_message' && am && typeof am.text === 'string') {
      text = am.text;
    } else if (typeof mt.text === 'string') {
      text = mt.text;
    } else if (am && typeof am.text === 'string') {
      text = am.text;
    }
    if (typeof text === 'string' && text.trim()) {
      return stripDraftMarkdownArtifacts(text);
    }
  }
  return '';
}

export function conversationIdFromCeResponse(payload: Record<string, unknown>): string {
  const cid = payload.conversation_id;
  return typeof cid === 'string' ? cid.trim() : '';
}
